import { Component, OnInit, OnDestroy, AfterViewInit,
         ChangeDetectionStrategy, ChangeDetectorRef, NgZone, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { StoreService } from '../../store.service';
import { DataService } from '../../data.service';
import { DealerService } from '../../dealer.service';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { TranslationService } from 'src/app/core/translation.service';
import { AiSectionConfig, parseAiInsightSections, AiInsightSection, AiInsightRow } from 'src/app/core/ai-summary-parser';
import { AI_SUMMARY_MIN_DURATION_MS, AI_SUMMARY_QUERY_PARAM } from 'src/app/core/session-replay-ai';

declare const rrweb: any;
declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback);
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}
type ResizeObserverCallback = (entries: ResizeObserverEntry[]) => void;
interface ResizeObserverEntry { readonly contentRect: DOMRectReadOnly; }

export interface PlayerTimelineEvent {
  id: number;
  type: 'click' | 'rage_click' | 'input' | 'navigate' | 'scroll' | 'meta';
  timeMs: number;
  percent: number;
  label: string;
  subLabel?: string;
  icon: string;
  badgeClass: string;
}

export interface SessionReplayAiMetrics {
  durationMs: number;
  formattedDuration: string;
  activeMs: number;
  formattedActiveTime: string;
  activePercent: number;
  inactiveMs: number;
  inactivePercent: number;
  totalInteractions: number;
  clickCount: number;
  inputCount: number;
  navCount: number;
  rageClickCount: number;
  frictionLevel: 'Smooth Flow' | 'Moderate Hesitation' | 'High Friction';
  deviceType: 'Mobile' | 'Tablet' | 'Desktop';
  landingPage: string;
  exitPage: string;
}

export interface InsightToken {
  text: string;
  isTimestamp?: boolean;
  isBold?: boolean;
  ms?: number;
}

const AI_SESSION_SUMMARY_SECTIONS: AiSectionConfig[] = [
  {
    theme: 'overview',
    title: 'Executive Session Overview',
    icon: 'fi-rr-chart-pie-alt',
    phrases: [
      'executive session overview',
      'executive overview',
      'session overview',
      'overview',
      'summary',
    ],
  },
  {
    theme: 'performers',
    title: 'Session Insights',
    icon: 'fi-rr-sparkles',
    phrases: [
      'session insights',
      'session insight',
      'insights',
      'key insights',
    ],
  },
];


let rrwebLoadPromise: Promise<void> | null = null;

@Component({
  selector: 'app-replay-player',
  templateUrl: './replay-player.component.html',
  styleUrls: ['./replay-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReplayPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  bearer: string;
  storeId: string;
  visitorId: string;
  sessionId: string;
  returnPage: number | null = null;
  returnVp: number | null = null;
  backQueryParams: any = {};

  events: any[]  = [];
  isLoading      = true;
  error: string  = null;

  // The storefront records one session per page; visit mode plays the
  // visitor's consecutive pages (home -> product -> checkout) as one replay.
  visitMode      = true;
  visitPageCount = 0;

  replayer: any  = null;
  playerReady    = false;
  playing        = false;
  speed          = 1;
  skipInactive   = true;
  totalMs        = 0;
  currentMs      = 0;

  formattedCurrent = '0:00';
  formattedTotal   = '0:00';
  progressPct      = 0;

  ticker: any    = null;
  playStartWall: number  = null;
  playStartOffset        = 0;
  isFullscreen           = false;
  isMobileSession        = false;
  isTabletSession        = false;
  isTabletLandscape      = false;
  sessionViewport: { width: number; height: number } | null = null;
  sessionUrl             = '';

  // Enhanced UI / UX State
  showActivityDrawer     = typeof window !== 'undefined' ? window.innerWidth >= 992 : true;
  activeActivityFilter   = 'all';
  activitySearchQuery    = '';
  timelineEvents: PlayerTimelineEvent[] = [];
  gapIntervals: { start: number; end: number; percentStart: number; percentWidth: number }[] = [];
  currentActiveEventId: number | null = null;
  showShortcutsModal     = false;
  copiedNotice           = '';

  // AI Assistant / Recording Summary & Insights State
  showAiSummaryModal     = false;
  isGeneratingAiSummary  = false;
  aiSummaryContent       = '';
  aiSummaryError: string | null = null;
  enableAIAssistant      = true;
  aiTokenCost: number | null = null;
  aiPurpose              = 'session replay summary';
  /** Set when the sessions list opened the player through its AI Summary button (?ai=1). */
  private autoOpenAiSummary = false;
  parsedSections: AiInsightSection[] = [];

  // AI summary feedback (stored in store_feedback under the "AI Session Replay Summary" topic)
  showFeedbackForm       = false;
  feedbackRating: 'helpful' | 'not_helpful' | null = null;
  feedbackComment        = '';
  isSubmittingFeedback   = false;
  feedbackSubmitted      = false;
  feedbackError: string | null = null;
  sessionMetrics: SessionReplayAiMetrics | null = null;

  // Hover Scrubber
  isHoveringScrubber     = false;
  hoverPercent           = 0;
  hoverFormattedTime     = '0:00';
  hoverEventLabel        = '';

  private rrwebReady      = false;
  private pendingPlay     = false;
  private skipCheckerId: any = null;
  private resizeTimer: any = null;
  private destroy$        = new Subject<void>();
  private resizeObserver: ResizeObserver | null = null;
  private naturalSessionW: number | null = null;
  private naturalSessionH: number | null = null;

  private resizeHandler = () => {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (!this.isLoading && !this.error && this.events.length) this.scalePlayer();
    }, 150);
  };

  constructor(
    private svc: StoreService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private dataService: DataService,
    private dealerService: DealerService,
    private utils: UtilitiesService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.bearer    = localStorage.getItem('bearer');
    this.storeId   = localStorage.getItem('store_id');
    this.visitorId = this.route.snapshot.paramMap.get('visitorId');
    this.sessionId = this.route.snapshot.paramMap.get('sessionId');
    const pageParam = Number(this.route.snapshot.queryParamMap.get('page'));
    this.returnPage = pageParam > 0 ? pageParam : null;
    const vpParam = Number(this.route.snapshot.queryParamMap.get('vp'));
    this.returnVp = vpParam > 0 ? vpParam : null;
    this.backQueryParams = { session: this.sessionId };
    if (this.returnPage) this.backQueryParams.page = this.returnPage;
    if (this.returnVp) this.backQueryParams.vp = this.returnVp;
    // Opened from the sessions list's AI Summary button: start the summary once the recording loads
    this.autoOpenAiSummary = this.route.snapshot.queryParamMap.get(AI_SUMMARY_QUERY_PARAM) === '1';

    // Hide events log drawer by default on small / mobile screens
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      this.showActivityDrawer = false;
    }

    this.loadAiCreditState();
    this.loadRrweb().then(() => this.loadSession());
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (!this.isLoading && !this.error && this.events.length) this.scalePlayer();
        }, 150);
      });
      const wrapper = document.querySelector('.wrapper-replay-player');
      if (wrapper) this.resizeObserver.observe(wrapper);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    window.removeEventListener('resize', this.resizeHandler);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.stopSkipChecker();
    this.stopTicker();
    if (this.replayer) {
      try {
        this.replayer.pause();
        if (typeof this.replayer.destroy === 'function') this.replayer.destroy();
      } catch {}
      this.replayer = null;
    }
    const root = document.getElementById('rr-player-root');
    if (root) root.innerHTML = '';
  }

  // ── Keyboard Shortcuts ──────────────────────────────────────────────────
  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }

    if (this.showAiSummaryModal) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeAiSummary();
      }
      return;
    }

    if (event.code === 'Space' || event.key === 'k') {
      event.preventDefault();
      this.playing ? this.pause() : this.play();
    } else if (event.key === 'ArrowLeft' || event.key === 'j') {
      event.preventDefault();
      this.rewind(10000);
    } else if (event.key === 'ArrowRight' || event.key === 'l') {
      event.preventDefault();
      this.forward(10000);
    } else if (event.key === 'f' || event.key === 'F') {
      event.preventDefault();
      this.isFullscreen ? this.closeFullscreen() : this.openFullscreen();
    } else if (event.key === 'm' || event.key === 'M') {
      event.preventDefault();
      this.changeSkipInactive(!this.skipInactive);
    } else if (['1', '2', '3', '4'].includes(event.key)) {
      const speedMap: { [key: string]: number } = { '1': 1, '2': 2, '3': 4, '4': 0.5 };
      this.setSpeed(speedMap[event.key]);
    } else if (event.key === '?') {
      if (typeof window !== 'undefined' && window.innerWidth >= 768) {
        this.showShortcutsModal = !this.showShortcutsModal;
        this.cdr.markForCheck();
      }
    }
  }

  private loadRrweb(): Promise<void> {
    if (typeof rrweb !== 'undefined') { this.rrwebReady = true; return Promise.resolve(); }
    if (!rrwebLoadPromise) {
      rrwebLoadPromise = new Promise((resolve, reject) => {
        const css = document.createElement('link');
        css.rel  = 'stylesheet';
        css.href = 'https://cdn.jsdelivr.net/npm/rrweb@1.1.3/dist/rrweb.min.css';
        document.head.appendChild(css);

        const script    = document.createElement('script');
        script.src      = 'https://cdn.jsdelivr.net/npm/rrweb@1.1.3/dist/rrweb.min.js';
        script.onload   = () => resolve();
        script.onerror  = (err) => {
          console.error('Failed to load rrweb library from CDN', err);
          resolve();
        };
        document.head.appendChild(script);
      });
    }
    return rrwebLoadPromise.then(() => { this.rrwebReady = true; });
  }

  private async getDealerCredentials(): Promise<{ dealerId: string; dealerUsername: string }> {
    const info = JSON.parse(localStorage.getItem('5s-info') || '{}');
    const directInfo = this.dataService?.getDealerInfo() || this.dealerService?.dealerData;
    let dealerId = info.d_id || info.dealer_id || directInfo?.dealer_id || directInfo?.d_id || '';
    let dealerUsername = info.username || directInfo?.username || localStorage.getItem('username') || localStorage.getItem('dealer_username') || '';

    if (!dealerUsername || !dealerId) {
      try {
        const fetched: any = await this.dealerService.getDealer().toPromise();
        if (fetched) {
          if (!dealerUsername) dealerUsername = fetched.username || '';
          if (!dealerId) dealerId = fetched.dealer_id || fetched.d_id || '';
        }
      } catch (e) {}
    }

    return { dealerId, dealerUsername };
  }

  private async trackMixpanelEvent(eventType: 'Session Replay Watch Success' | 'Session Replay Watch Fail', errorResponse?: string): Promise<void> {
    try {
      const { dealerId, dealerUsername } = await this.getDealerCredentials();

      const dataEvent: any = {
        data: {
          d_id: dealerId,
          type: eventType,
          distinct_id: dealerId,
          dealer_username: dealerUsername,
          store_id: this.storeId,
          visitor_id: this.visitorId,
          session_id: this.sessionId,
          platform: 'dashboard',
        },
        application: 'dashboard',
        driver: 'mixpanel',
      };

      if (errorResponse) {
        dataEvent.data.error_response = errorResponse;
      }

      this.svc.publish_event(dataEvent).subscribe(
        () => {},
        (err) => console.log('Mixpanel tracking error:', err)
      );
    } catch (e) {
      console.log('Error tracking mixpanel event in player:', e);
    }
  }

  setVisitMode(visitMode: boolean): void {
    if (this.visitMode === visitMode) return;
    this.visitMode = visitMode;
    if (this.playing) this.pause();
    this.loadSession();
  }

  loadSession(): void {
    this.isLoading = true;
    this.error     = null;
    const request$ = this.visitMode
      ? this.svc.getReplayVisitEvents(this.bearer, this.sessionId, this.storeId).pipe(
          // An API without the visit endpoint yet: fall back to this page only.
          catchError(() => {
            this.visitMode = false;
            return this.svc.getReplayEvents(this.bearer, this.sessionId, this.storeId);
          })
        )
      : this.svc.getReplayEvents(this.bearer, this.sessionId, this.storeId);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          if (this.visitMode) {
            this.visitPageCount = res?.data?.sessions?.length ?? 0;
            // The visit is found from the session's first chunk; without one it
            // comes back empty even when later chunks have events.
            if (!res?.data?.events?.length) {
              this.setVisitMode(false);
              return;
            }
          }
          this.events    = this.patchSandboxedIframes(res?.data?.events ?? []);
          this.isLoading = false;
          if (this.events.length > 0) {
            this.detectSessionType(this.events);
            this.parseTimelineEvents(this.events);
            this.trackMixpanelEvent('Session Replay Watch Success');
            setTimeout(() => {
              this.initPlayer();
              this.runPendingAiSummary();
            }, 0);
          } else {
            this.error = 'No events found for this session.';
            this.trackMixpanelEvent('Session Replay Watch Fail', 'No events found for this session');
          }
          this.cdr.markForCheck();
        },
        (err) => {
          this.error     = 'Failed to load session.';
          this.isLoading = false;
          this.trackMixpanelEvent('Session Replay Watch Fail', err?.message || 'API error / Network failure');
          this.cdr.markForCheck();
        }
      );
  }

  private detectSessionType(events: any[]): void {
    const metaEvent = events.find(e => e.type === 4);
    const width  = metaEvent?.data?.width;
    const height = metaEvent?.data?.height;
    const href   = metaEvent?.data?.href;

    if (typeof width === 'number' && typeof height === 'number') {
      this.sessionViewport = { width, height };
    }
    if (href) {
      try { this.sessionUrl = new URL(href).hostname || href; } catch { this.sessionUrl = href; }
    }

    this.isMobileSession   = typeof width === 'number' && width <= 768;
    this.isTabletSession   = typeof width === 'number' && width > 768 && width <= 1280;
    this.isTabletLandscape = this.isTabletSession && typeof height === 'number' && width > height;

    if (this.isMobileSession || (typeof window !== 'undefined' && window.innerWidth < 992)) {
      this.showActivityDrawer = false;
    }
  }

  private parseTimelineEvents(events: any[]): void {
    if (!events.length) return;
    const t0 = events[0].timestamp;
    const tEnd = events[events.length - 1].timestamp;
    const duration = Math.max(tEnd - t0, 1);

    const nodeMap = this.buildRrwebNodeMap(events);
    const parsed: PlayerTimelineEvent[] = [];
    const clickHistory: { time: number; x: number; y: number }[] = [];
    let idCounter = 1;

    events.forEach((e) => {
      const timeOffset = Math.max(0, e.timestamp - t0);
      const pct = (timeOffset / duration) * 100;

      // Meta / Page Navigation
      if (e.type === 4 && e.data?.href) {
        let cleanPath = e.data.href;
        try { cleanPath = new URL(e.data.href).pathname || cleanPath; } catch {}
        parsed.push({
          id: idCounter++,
          type: 'navigate',
          timeMs: timeOffset,
          percent: pct,
          label: 'Visited ' + cleanPath,
          subLabel: e.data.href,
          icon: 'fi-rr-globe',
          badgeClass: 'badge-navigate',
        });
      }

      // Incremental Snapshots
      if (e.type === 3 && e.data) {
        // Mouse Interactions (Clicks)
        if (e.data.source === 2) {
          // Only Click (2): the recorder also emits MouseUp (0) for the same click,
          // which would count every click twice and flag double-clicks as rage clicks
          const mouseType = e.data.type;
          if (mouseType === 2) {
            const x = e.data.x || 0;
            const y = e.data.y || 0;

            const clickDesc = this.describeElement(e.data.id, nodeMap, x, y);

            // Rage click detection: 3+ clicks within 1000ms within 40px radius
            clickHistory.push({ time: timeOffset, x, y });
            const recentClicks = clickHistory.filter(c => timeOffset - c.time <= 1000 && Math.hypot(c.x - x, c.y - y) < 40);

            if (recentClicks.length >= 3) {
              const targetName = clickDesc.label !== 'Clicked on screen'
                ? clickDesc.label.replace(/^Clicked\s+/i, '')
                : '';
              parsed.push({
                id: idCounter++,
                type: 'rage_click',
                timeMs: timeOffset,
                percent: pct,
                label: targetName ? `Rapid Clicks on ${targetName}` : 'Rapid Clicks (Rage Click)',
                subLabel: clickDesc.subLabel,
                icon: 'fi-rr-exclamation',
                badgeClass: 'badge-rage',
              });
            } else {
              parsed.push({
                id: idCounter++,
                type: 'click',
                timeMs: timeOffset,
                percent: pct,
                label: clickDesc.label,
                subLabel: clickDesc.subLabel,
                icon: 'fi-rr-mouse',
                badgeClass: 'badge-click',
              });
            }
          }
        }

        // Input Changes
        if (e.data.source === 5) {
          const inputDesc = this.describeInputElement(e.data.id, nodeMap, e.data.text);
          parsed.push({
            id: idCounter++,
            type: 'input',
            timeMs: timeOffset,
            percent: pct,
            label: inputDesc.label,
            subLabel: inputDesc.subLabel,
            icon: 'fi-rr-edit',
            badgeClass: 'badge-input',
          });
        }
      }
    });

    this.timelineEvents = parsed;
  }

  private buildRrwebNodeMap(events: any[]): Map<number, any> {
    const nodeMap = new Map<number, any>();

    const indexNode = (node: any, parentId: number | null) => {
      if (!node || typeof node.id !== 'number') return;

      const existing = nodeMap.get(node.id);
      const nodeInfo = {
        id: node.id,
        type: node.type,
        tagName: (node.tagName || '').toLowerCase(),
        attributes: { ...(existing?.attributes || {}), ...(node.attributes || {}) },
        textContent: (node.textContent !== undefined ? node.textContent : (existing?.textContent || '')).toString(),
        parentId: parentId !== null ? parentId : (existing?.parentId ?? null),
        childIds: existing?.childIds ? [...existing.childIds] : [],
      };

      nodeMap.set(node.id, nodeInfo);

      if (parentId !== null && nodeMap.has(parentId)) {
        const parent = nodeMap.get(parentId);
        if (parent) {
          if (!parent.childIds) parent.childIds = [];
          if (!parent.childIds.includes(node.id)) {
            parent.childIds.push(node.id);
          }
        }
      }

      if (Array.isArray(node.childNodes)) {
        for (const child of node.childNodes) {
          if (child && typeof child.id === 'number') {
            if (!nodeInfo.childIds.includes(child.id)) {
              nodeInfo.childIds.push(child.id);
            }
            indexNode(child, node.id);
          }
        }
      }
    };

    for (const e of events) {
      if (e.type === 2 && e.data?.node) {
        indexNode(e.data.node, null);
      } else if (e.type === 3 && e.data?.source === 0) {
        if (Array.isArray(e.data.adds)) {
          for (const add of e.data.adds) {
            if (add?.node) {
              indexNode(add.node, add.parentId ?? null);
            }
          }
        }
        if (Array.isArray(e.data.texts)) {
          for (const t of e.data.texts) {
            if (t && typeof t.id === 'number' && nodeMap.has(t.id)) {
              const item = nodeMap.get(t.id);
              if (item) item.textContent = (t.value || '').toString();
            }
          }
        }
        if (Array.isArray(e.data.attributes)) {
          for (const a of e.data.attributes) {
            if (a && typeof a.id === 'number' && nodeMap.has(a.id)) {
              const item = nodeMap.get(a.id);
              if (item) item.attributes = { ...item.attributes, ...(a.attributes || {}) };
            }
          }
        }
      }
    }

    return nodeMap;
  }

  private getNodeTextContent(nodeId: number, nodeMap: Map<number, any>, depth = 0): string {
    if (depth > 4) return '';
    const node = nodeMap.get(nodeId);
    if (!node) return '';

    let text = '';
    if (node.type === 3 || (!node.childIds?.length && node.textContent)) {
      text += ' ' + (node.textContent || '');
    }
    if (node.childIds?.length) {
      for (const cid of node.childIds) {
        text += ' ' + this.getNodeTextContent(cid, nodeMap, depth + 1);
      }
    }
    return text.replace(/\s+/g, ' ').trim();
  }

  private resolveTargetElement(nodeId: number, nodeMap: Map<number, any>): any {
    if (!nodeMap.has(nodeId)) return null;
    const initial = nodeMap.get(nodeId);
    let curr: any = initial;
    let candidate: any = initial;
    let depth = 0;

    while (curr && depth < 6) {
      const tag = curr.tagName || '';
      const role = (curr.attributes?.role || '').toLowerCase();
      const isInteractiveTag = ['button', 'a', 'input', 'select', 'textarea', 'summary', 'label', 'option'].includes(tag);
      const isInteractiveRole = ['button', 'link', 'tab', 'menuitem', 'checkbox', 'radio', 'switch', 'option'].includes(role);
      const isButtonClass = typeof curr.attributes?.class === 'string' &&
        /(?:^|\s)(?:btn|button|nav-link|nav-item|menu-item|tab-btn|tab-item|action-btn)(?:\s|$)/i.test(curr.attributes.class);

      if (isInteractiveTag || isInteractiveRole || isButtonClass) {
        return curr;
      }

      if (['path', 'svg', 'g', 'i', 'use', 'span'].includes(tag) && curr.parentId !== null) {
        const p = nodeMap.get(curr.parentId);
        if (p) candidate = p;
      }

      if (curr.parentId !== null) {
        curr = nodeMap.get(curr.parentId);
      } else {
        break;
      }
      depth++;
    }

    return candidate;
  }

  private describeElement(
    nodeId: number | undefined,
    nodeMap: Map<number, any>,
    x: number,
    y: number
  ): { label: string; subLabel: string } {
    const posStr = `(${x}, ${y})`;
    if (nodeId === undefined || !nodeMap.has(nodeId)) {
      return {
        label: 'Clicked on screen',
        subLabel: `Position ${posStr}`,
      };
    }

    const node = this.resolveTargetElement(nodeId, nodeMap) || nodeMap.get(nodeId);
    const tag = (node.tagName || '').toLowerCase();
    const attrs = node.attributes || {};

    const ariaLabel = (attrs['aria-label'] || attrs.title || attrs.alt || '').toString().trim();
    const innerText = this.getNodeTextContent(node.id, nodeMap).slice(0, 36);
    const placeholder = (attrs.placeholder || '').toString().trim();
    const nameOrId = (attrs.name || attrs.id || '').toString().trim();
    const value = (attrs.value || '').toString().trim();

    // Build CSS selector for subLabel
    let selector = tag || 'element';
    if (attrs.id) {
      selector += `#${attrs.id}`;
    } else if (attrs.class && typeof attrs.class === 'string') {
      const classes = attrs.class
        .trim()
        .split(/\s+/)
        .filter((c: string) => c && !c.startsWith('ng-') && !c.startsWith('_ng') && c.length < 30)
        .slice(0, 2);
      if (classes.length) {
        selector += `.${classes.join('.')}`;
      }
    }

    const subLabel = selector ? `${selector} • ${posStr}` : `Position ${posStr}`;

    // Tag specific labels
    if (tag === 'button' || attrs.role === 'button' || (typeof attrs.class === 'string' && attrs.class.includes('btn'))) {
      const btnText = innerText || ariaLabel || value || nameOrId;
      if (btnText) {
        return { label: `Clicked "${btnText}" button`, subLabel };
      }
      return { label: `Clicked button ${selector}`, subLabel };
    }

    if (tag === 'a' || attrs.role === 'link') {
      const linkText = innerText || ariaLabel;
      if (linkText) {
        return { label: `Clicked "${linkText}" link`, subLabel };
      }
      if (attrs.href) {
        let href = attrs.href.toString();
        try { href = new URL(href, 'https://dummy.com').pathname || href; } catch {}
        return { label: `Clicked link "${href}"`, subLabel };
      }
      return { label: `Clicked link ${selector}`, subLabel };
    }

    if (tag === 'input') {
      const type = (attrs.type || 'text').toString().toLowerCase();
      if (type === 'submit' || type === 'button') {
        const val = value || ariaLabel || 'Submit';
        return { label: `Clicked "${val}" button`, subLabel };
      }
      if (type === 'checkbox') {
        const name = ariaLabel || nameOrId || innerText;
        return { label: name ? `Clicked "${name}" checkbox` : 'Clicked checkbox', subLabel };
      }
      if (type === 'radio') {
        const name = ariaLabel || value || nameOrId;
        return { label: name ? `Clicked "${name}" radio` : 'Clicked radio option', subLabel };
      }
      const desc = placeholder || ariaLabel || nameOrId || type;
      return { label: `Clicked input "${desc}"`, subLabel };
    }

    if (tag === 'select') {
      const name = ariaLabel || nameOrId || 'dropdown';
      return { label: `Clicked select "${name}"`, subLabel };
    }

    if (tag === 'option') {
      const optText = innerText || value || 'option';
      return { label: `Selected "${optText}"`, subLabel };
    }

    if (tag === 'img') {
      const altText = ariaLabel || attrs.alt;
      if (altText) {
        return { label: `Clicked image "${altText}"`, subLabel };
      }
      return { label: 'Clicked image', subLabel };
    }

    if (tag === 'label') {
      const lText = innerText || ariaLabel || nameOrId;
      if (lText) {
        return { label: `Clicked "${lText}" label`, subLabel };
      }
      return { label: 'Clicked form label', subLabel };
    }

    // Generic elements (p, h1-h6, div, span, li, etc.)
    if (innerText) {
      return { label: `Clicked "${innerText}"`, subLabel };
    }
    if (ariaLabel) {
      return { label: `Clicked "${ariaLabel}"`, subLabel };
    }
    if (attrs.id || (attrs.class && typeof attrs.class === 'string')) {
      return { label: `Clicked ${selector}`, subLabel };
    }

    return { label: 'Clicked on screen', subLabel: `Position ${posStr}` };
  }

  private describeInputElement(nodeId: number | undefined, nodeMap: Map<number, any>, textValue?: string): { label: string; subLabel: string } {
    let inputName = '';
    let selector = 'input';
    if (nodeId !== undefined && nodeMap.has(nodeId)) {
      const node = nodeMap.get(nodeId);
      if (node) {
        const attrs = node.attributes || {};
        const tag = (node.tagName || 'input').toLowerCase();
        const placeholder = (attrs.placeholder || '').toString().trim();
        const name = (attrs.name || attrs.id || '').toString().trim();
        const aria = (attrs['aria-label'] || attrs.title || '').toString().trim();
        inputName = placeholder || aria || name;

        selector = tag;
        if (attrs.id) {
          selector += `#${attrs.id}`;
        } else if (attrs.name) {
          selector += `[name="${attrs.name}"]`;
        } else if (attrs.class && typeof attrs.class === 'string') {
          const cls = attrs.class.trim().split(/\s+/)[0];
          if (cls) selector += `.${cls}`;
        }
      }
    }

    const label = inputName ? `Typed in "${inputName}"` : 'Form Input interaction';
    const subLabel = textValue ? `Value: ${textValue.replace(/./g, '•')}` : (selector ? `${selector} modified` : 'Input modified');

    return { label, subLabel };
  }

  get filteredActivityEvents(): PlayerTimelineEvent[] {
    let list = this.timelineEvents;
    if (this.activeActivityFilter !== 'all') {
      if (this.activeActivityFilter === 'clicks') {
        list = list.filter(e => e.type === 'click' || e.type === 'rage_click');
      } else if (this.activeActivityFilter === 'inputs') {
        list = list.filter(e => e.type === 'input');
      } else if (this.activeActivityFilter === 'navs') {
        list = list.filter(e => e.type === 'navigate');
      }
    }
    if (this.activitySearchQuery.trim()) {
      const q = this.activitySearchQuery.toLowerCase().trim();
      list = list.filter(e => e.label.toLowerCase().includes(q) || (e.subLabel && e.subLabel.toLowerCase().includes(q)));
    }
    return list;
  }

  initPlayer(autoPlay = true): void {
    if (!this.rrwebReady || !this.events.length) return;
    const root = document.getElementById('rr-player-root');
    if (!root) return;

    if (this.replayer) {
      try { this.replayer.pause(); } catch {}
      this.replayer = null;
    }
    root.innerHTML = '';
    this.naturalSessionW = null;
    this.naturalSessionH = null;

    const startTs  = this.events[0].timestamp;
    const endTs    = this.events[this.events.length - 1].timestamp;
    this.totalMs         = Math.max(0, endTs - startTs);
    this.currentMs       = 0;
    this.formattedTotal  = this.formatTime(this.totalMs);
    this.formattedCurrent = '0:00';
    this.progressPct     = 0;

    // Cache measured duration so session list reflects true duration
    try {
      if (this.sessionId && this.totalMs > 0 && !(this.visitMode && this.visitPageCount > 1)) {
        sessionStorage.setItem('replay_dur_' + this.sessionId, String(this.totalMs));
      }
    } catch {}

    // Pre-compute inactive gaps (>10 s)
    const SKIP_THRESHOLD = 10_000;
    const t0 = this.events[0].timestamp;
    this.gapIntervals = [];
    for (let i = 1; i < this.events.length; i++) {
      const gap = this.events[i].timestamp - this.events[i - 1].timestamp;
      if (gap > SKIP_THRESHOLD) {
        const start = this.events[i - 1].timestamp - t0;
        const end   = this.events[i].timestamp     - t0;
        this.gapIntervals.push({
          start,
          end,
          percentStart: (start / this.totalMs) * 100,
          percentWidth: ((end - start) / this.totalMs) * 100,
        });
      }
    }

    // Safe iframe sandbox interceptor with try...finally
    const _origSetAttr = Element.prototype.setAttribute;
    try {
      (Element.prototype as any).setAttribute = function(name: string, value: string) {
        if (name === 'sandbox' && !value.includes('allow-scripts')) {
          value = (value.trim() ? value + ' ' : '') + 'allow-scripts';
        }
        return _origSetAttr.call(this, name, value);
      };
      this.replayer = new rrweb.Replayer(this.events, {
        root,
        speed:        this.speed,
        skipInactive: this.skipInactive,
        showWarning:  false,
        showDebug:    false,
      });
    } finally {
      Element.prototype.setAttribute = _origSetAttr;
    }

    this.replayer.on('finish', () => this.ngZone.run(() => this.onFinish()));

    if (autoPlay) this.pendingPlay = true;
    this.scalePlayer();
  }

  private scalePlayer(attempt = 0): void {
    const root = document.getElementById('rr-player-root');
    const clip = root?.parentElement;
    if (!root || !clip) return;

    const pw     = clip.closest('.player-card') as HTMLElement;
    const parent = pw?.parentElement;
    const parentW = parent ? parent.clientWidth : 0;

    const wrapper  = root.querySelector('.replayer-wrapper') as HTMLElement;

    if (!this.naturalSessionW || !this.naturalSessionH) {
      const rawW = this.sessionViewport?.width || wrapper?.offsetWidth;
      const rawH = this.sessionViewport?.height || wrapper?.offsetHeight;

      if (!rawW || !rawH) {
        if (attempt === 0) requestAnimationFrame(() => this.scalePlayer(1));
        else if (attempt < 10) setTimeout(() => this.scalePlayer(attempt + 1), 100);
        else { this.playerReady = true; this.cdr.markForCheck(); }
        return;
      }
      this.naturalSessionW = rawW;
      this.naturalSessionH = rawH;
    }

    const naturalW = this.naturalSessionW;
    const naturalH = this.naturalSessionH;

    const pwH = pw ? Math.max(pw.clientHeight, 300) : 300;

    const controlsBarEl  = pw?.querySelector('.controls-bar')        as HTMLElement;
    const progressWrapEl = pw?.querySelector('.progress-wrap')       as HTMLElement;
    const headerBarEl    = pw?.querySelector('.player-top-header')   as HTMLElement;
    const controlsH = (controlsBarEl?.offsetHeight ?? 56) + (progressWrapEl?.offsetHeight ?? 41) + (headerBarEl?.offsetHeight ?? 48);

    const area  = clip.closest('.player-area') as HTMLElement;
    const areaW = area ? area.clientWidth : (parentW || 400);

    const phoneFrame   = document.querySelector('.phone-frame')            as HTMLElement;
    const tabletFrame  = document.querySelector('.tablet-frame')           as HTMLElement;
    const browserFrame = document.querySelector('.browser-frame')          as HTMLElement;
    const tabBarEl     = document.querySelector('.browser-frame__tab-bar') as HTMLElement;
    const screenEl     = document.querySelector('.browser-frame__screen')  as HTMLElement;
    const tabBarH = tabBarEl?.offsetHeight ?? 37;

    const areaH = Math.max(pwH - controlsH, 200);

    let scale: number;

    if (this.isMobileSession) {
      const CHROME_H = 72, CHROME_W = 20, AREA_PAD = 24;
      const availW = Math.max(areaW - CHROME_W - AREA_PAD, 100);
      const availH = Math.max(areaH - CHROME_H - AREA_PAD, 100);
      scale = Math.max(Math.min(availW / naturalW, availH / naturalH), 0.1);
      if (phoneFrame) phoneFrame.style.height = 'auto';
    } else if (this.isTabletSession) {
      const CHROME_H = this.isTabletLandscape ? 20 : 40;
      const CHROME_W = this.isTabletLandscape ? 56 : 32;
      const AREA_PAD = 24;
      const availW = Math.max(areaW - CHROME_W - AREA_PAD, 100);
      const availH = Math.max(areaH - CHROME_H - AREA_PAD, 100);
      scale = Math.max(Math.min(availW / naturalW, availH / naturalH), 0.1);
      if (tabletFrame) tabletFrame.style.height = 'auto';
    } else {
      if (browserFrame) {
        browserFrame.style.height = '100%';
        browserFrame.style.width  = '100%';
      }
      const PAD = 24;
      const screenAreaH = Math.max(areaH - tabBarH, 100);
      const availableW  = Math.max(areaW - PAD, 100);
      const availableH  = Math.max(screenAreaH - PAD, 100);

      const widthScale  = availableW / naturalW;
      const heightScale = availableH / naturalH;

      // Always fit proportionally within both width & height so content is NEVER stretched or cropped
      scale = Math.max(Math.min(widthScale, heightScale), 0.05);

      if (screenEl) {
        screenEl.style.overflow = 'hidden';
      }
    }

    (root as any).style.zoom   = String(scale);
    root.style.transform       = '';
    root.style.transformOrigin = '';
    root.style.width           = '';

    this.playerReady = true;
    this.cdr.markForCheck();

    if (this.pendingPlay) {
      this.pendingPlay = false;
      this.play();
    }
  }

  play(): void {
    if (!this.replayer || this.playing) return;
    this.replayer.play(this.currentMs);
    this.playing = true;
    this.startTicker();
    this.startSkipChecker();
    this.cdr.markForCheck();
  }

  pause(): void {
    if (!this.replayer || !this.playing) return;
    this.replayer.pause();
    this.playing = false;
    this.stopTicker();
    this.stopSkipChecker();
    this.cdr.markForCheck();
  }

  private startSkipChecker(): void {
    this.stopSkipChecker();
    if (!this.skipInactive || !this.gapIntervals.length) return;
    this.ngZone.runOutsideAngular(() => {
      this.skipCheckerId = setInterval(() => {
        if (!this.playing || !this.replayer) return;
        for (const gap of this.gapIntervals) {
          if (this.currentMs > gap.start && this.currentMs < gap.end - 500) {
            const jumpTo = gap.end + 100;
            this.stopTicker();
            this.playStartOffset = jumpTo;
            this.playStartWall   = Date.now();
            this.currentMs       = jumpTo;
            this.replayer.play(jumpTo);
            this.playing = true;
            this.startTicker();
            this.ngZone.run(() => this.cdr.markForCheck());
            break;
          }
        }
      }, 250);
    });
  }

  private stopSkipChecker(): void {
    if (this.skipCheckerId) { clearInterval(this.skipCheckerId); this.skipCheckerId = null; }
  }

  restart(): void {
    this.stopTicker();
    this.stopSkipChecker();
    this.playing          = false;
    this.playerReady      = false;
    this.playStartOffset  = 0;
    this.currentMs        = 0;
    this.formattedCurrent = '0:00';
    this.progressPct      = 0;
    this.initPlayer(false);
  }

  seekTo(timeMs: number): void {
    if (!this.replayer) return;
    const wasPlaying = this.playing;
    this.pause();

    this.currentMs = Math.max(0, Math.min(timeMs, this.totalMs));
    this.playStartOffset = this.currentMs;
    this.progressPct = this.totalMs > 0 ? (this.currentMs / this.totalMs) * 100 : 0;
    this.formattedCurrent = this.formatTime(this.currentMs);
    this.updateActiveEvent();

    if (wasPlaying) {
      this.play();
    } else {
      this.replayer.play(this.currentMs);
      this.replayer.pause();
    }
    this.cdr.markForCheck();
  }

  rewind(ms = 10000): void {
    this.seekTo(this.currentMs - ms);
  }

  forward(ms = 10000): void {
    this.seekTo(this.currentMs + ms);
  }

  onProgressClick(event: MouseEvent): void {
    const track = event.currentTarget as HTMLElement;
    const rect = track.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const pct = Math.max(0, Math.min(clickX / rect.width, 1));
    this.seekTo(pct * this.totalMs);
  }

  onScrubberMouseMove(event: MouseEvent): void {
    const track = event.currentTarget as HTMLElement;
    const rect = track.getBoundingClientRect();
    const hoverX = event.clientX - rect.left;
    const pct = Math.max(0, Math.min(hoverX / rect.width, 1));
    this.isHoveringScrubber = true;
    this.hoverPercent = pct * 100;
    const hoverMs = pct * this.totalMs;
    this.hoverFormattedTime = this.formatTime(hoverMs);

    // Find nearest event
    const nearest = this.timelineEvents.find(e => Math.abs(e.timeMs - hoverMs) < 3000);
    this.hoverEventLabel = nearest ? nearest.label : '';
  }

  get hoverTooltipTransform(): string {
    if (this.hoverPercent > 85) return 'translateX(-88%)';
    if (this.hoverPercent < 15) return 'translateX(-12%)';
    return 'translateX(-50%)';
  }

  onScrubberMouseLeave(): void {
    this.isHoveringScrubber = false;
  }

  restartAndPlay(): void {
    this.pendingPlay = true;
    this.restart();
  }

  onFinish(): void {
    this.playing          = false;
    this.currentMs        = this.totalMs;
    this.progressPct      = 100;
    this.formattedCurrent = this.formattedTotal;
    this.stopTicker();
    this.stopSkipChecker();
    this.cdr.markForCheck();
  }

  setSpeed(s: number): void {
    if (this.speed === s) return;
    this.speed = s;
    const wasPlaying = this.playing;
    if (this.playing) this.pause();
    if (wasPlaying) this.pendingPlay = true;
    this.restart();
  }

  changeSkipInactive(val: boolean): void {
    this.skipInactive = val;
    if (val) {
      if (this.playing) this.startSkipChecker();
    } else {
      this.stopSkipChecker();
    }
    this.cdr.markForCheck();
  }

  toggleActivityDrawer(): void {
    this.showActivityDrawer = !this.showActivityDrawer;
    this.cdr.markForCheck();
    setTimeout(() => this.scalePlayer(), 100);
  }

  private startTicker(): void {
    this.playStartWall = Date.now();
    this.ngZone.runOutsideAngular(() => {
      this.ticker = setInterval(() => {
        const elapsed     = this.playStartOffset + (Date.now() - this.playStartWall) * this.speed;
        this.currentMs    = Math.min(elapsed, this.totalMs);
        this.progressPct      = this.totalMs > 0 ? (this.currentMs / this.totalMs) * 100 : 0;
        this.formattedCurrent = this.formatTime(this.currentMs);
        this.updateActiveEvent();
        this.cdr.markForCheck();
      }, 80);
    });
  }

  private updateActiveEvent(): void {
    let activeId: number | null = null;
    for (let i = this.timelineEvents.length - 1; i >= 0; i--) {
      if (this.timelineEvents[i].timeMs <= this.currentMs) {
        activeId = this.timelineEvents[i].id;
        break;
      }
    }
    this.currentActiveEventId = activeId;
  }

  private stopTicker(): void {
    if (this.ticker) { clearInterval(this.ticker); this.ticker = null; }
    if (this.playStartWall !== null) {
      this.playStartOffset += (Date.now() - this.playStartWall) * this.speed;
      this.playStartWall    = null;
    }
  }

  private patchSandboxedIframes(events: any[]): any[] {
    return events.map(event => {
      if (event.type === 2) {
        this.patchNode(event.data?.node);
      } else if (event.type === 3 && event.data?.source === 0) {
        (event.data.adds ?? []).forEach((add: any) => this.patchNode(add.node));
      }
      return event;
    });
  }

  private patchNode(node: any): void {
    if (!node) { return; }
    if (node.tagName === 'iframe' && node.attributes?.sandbox !== undefined) {
      const s = String(node.attributes.sandbox ?? '');
      if (!s.includes('allow-scripts')) {
        node.attributes.sandbox = s ? s + ' allow-scripts' : 'allow-scripts';
      }
    }
    (node.childNodes ?? []).forEach((child: any) => this.patchNode(child));
  }

  formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  copyInfo(text: string, label: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.copiedNotice = label;
      this.cdr.markForCheck();
      setTimeout(() => {
        if (this.copiedNotice === label) {
          this.copiedNotice = '';
          this.cdr.markForCheck();
        }
      }, 2000);
    });
  }

  openFullscreen(): void {
    this.isFullscreen = true;
    this.cdr.markForCheck();
    setTimeout(() => this.scalePlayer(), 0);
  }

  closeFullscreen(): void {
    this.isFullscreen = false;
    this.cdr.markForCheck();
    setTimeout(() => this.scalePlayer(), 0);
  }

  // ── AI Recording Summary & Insights ───────────────────────────────────────
  loadAiCreditState(): void {
    this.dealerService.getAiCreditState('AI_REPLAY_SUMMARY').pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ enabled, tokenCost }) => {
        this.enableAIAssistant = enabled;
        this.aiTokenCost = tokenCost;
        this.cdr.markForCheck();
      },
      error: () => {
        this.enableAIAssistant = true;
        this.cdr.markForCheck();
      },
    });
  }

  private trackAiGenerate(event: 'AI Generate Success' | 'AI Generate Fail', errorResponse?: string): void {
    this.svc.trackAiGenerateEvent(event, {
      aiPurpose: this.aiPurpose,
      storeId: this.storeId,
      token: this.aiTokenCost || 0,
      errorResponse,
    });
  }

  get sessionDurationMs(): number {
    if (this.totalMs > 0) return this.totalMs;
    if (this.events && this.events.length >= 2) {
      const start = this.events[0]?.timestamp || 0;
      const end = this.events[this.events.length - 1]?.timestamp || 0;
      return Math.max(0, end - start);
    }
    return 0;
  }

  get isAiSummaryDisabled(): boolean {
    if (this.isLoading || this.isGeneratingAiSummary) {
      return true;
    }
    return this.sessionDurationMs < AI_SUMMARY_MIN_DURATION_MS;
  }

  get aiSummaryTooltip(): string {
    if (this.isLoading) {
      return 'Loading recording...';
    }
    if (this.isGeneratingAiSummary) {
      return 'Analyzing recording...';
    }
    if (this.sessionDurationMs < AI_SUMMARY_MIN_DURATION_MS) {
      return 'AI summary requires a recording of at least 10 seconds';
    }
    return 'Summarize recording & generate AI insights';
  }

  /**
   * Runs the summary requested by the sessions list (?ai=1), once: after initPlayer() has
   * measured the recording, so the same 10-second rule applies to the real duration. The param
   * is removed from the URL straight away so a refresh does not generate (and charge) again.
   * A recording that turns out shorter than the minimum just opens with the button disabled.
   */
  private runPendingAiSummary(): void {
    if (!this.autoOpenAiSummary) return;
    this.autoOpenAiSummary = false;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [AI_SUMMARY_QUERY_PARAM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    if (!this.isAiSummaryDisabled) {
      this.openAiSummary();
    }
  }

  openAiSummary(event?: Event): void {
    if (this.isAiSummaryDisabled) {
      return;
    }
    this.showAiSummaryModal = true;
    this.sessionMetrics = this.computeSessionMetrics();
    if (!this.aiSummaryContent && !this.isGeneratingAiSummary) {
      this.generateAiSummary(event);
    }
    this.cdr.markForCheck();
  }

  closeAiSummary(): void {
    this.showAiSummaryModal = false;
    this.cdr.markForCheck();
  }

  computeSessionMetrics(): SessionReplayAiMetrics {
    const totalMs = this.totalMs || 1;
    let totalInactiveMs = 0;
    for (const gap of this.gapIntervals) {
      totalInactiveMs += (gap.end - gap.start);
    }
    const activeMs = Math.max(0, totalMs - totalInactiveMs);
    const activePercent = Math.min(100, Math.round((activeMs / totalMs) * 100));
    const inactivePercent = 100 - activePercent;

    const clicks = this.timelineEvents.filter(e => e.type === 'click');
    const rageClicks = this.timelineEvents.filter(e => e.type === 'rage_click');
    const rageBursts = this.getRageBursts();
    const inputs = this.getFieldEntries();
    const navs = this.timelineEvents.filter(e => e.type === 'navigate');

    let frictionLevel: 'Smooth Flow' | 'Moderate Hesitation' | 'High Friction' = 'Smooth Flow';
    if (rageBursts.length >= 2 || (rageBursts.length >= 1 && inactivePercent > 50)) {
      frictionLevel = 'High Friction';
    } else if (rageBursts.length === 1 || inactivePercent > 35) {
      frictionLevel = 'Moderate Hesitation';
    }

    const landingPage = navs.length > 0 ? (navs[0].subLabel || navs[0].label) : (this.sessionUrl || 'Home');
    const exitPage = navs.length > 0 ? (navs[navs.length - 1].subLabel || navs[navs.length - 1].label) : (this.sessionUrl || 'Home');

    return {
      durationMs: this.totalMs,
      formattedDuration: this.formattedTotal,
      activeMs,
      formattedActiveTime: this.formatTime(activeMs),
      activePercent,
      inactiveMs: totalInactiveMs,
      inactivePercent,
      totalInteractions: clicks.length + rageClicks.length + inputs.length + navs.length,
      clickCount: clicks.length + rageClicks.length,
      inputCount: inputs.length,
      navCount: navs.length,
      rageClickCount: rageBursts.length,
      frictionLevel,
      deviceType: this.isMobileSession ? 'Mobile' : (this.isTabletSession ? 'Tablet' : 'Desktop'),
      landingPage,
      exitPage,
    };
  }

  /**
   * Every click from the 3rd onward in a fast burst is flagged as a rage click,
   * so group rage clicks less than a second apart into one burst (timed at its start).
   */
  private getRageBursts(): PlayerTimelineEvent[] {
    const bursts: PlayerTimelineEvent[] = [];
    let lastRageMs = -Infinity;
    for (const e of this.timelineEvents) {
      if (e.type !== 'rage_click') continue;
      if (e.timeMs - lastRageMs > 1000) bursts.push(e);
      lastRageMs = e.timeMs;
    }
    return bursts;
  }

  /**
   * The recorder emits one input event per keystroke, so collapse consecutive events
   * on the same field into a single entry (timed at the first keystroke).
   */
  private getFieldEntries(): PlayerTimelineEvent[] {
    const entries: PlayerTimelineEvent[] = [];
    let lastInput: PlayerTimelineEvent | null = null;
    for (const e of this.timelineEvents) {
      if (e.type === 'input') {
        if (!lastInput || lastInput.label !== e.label) entries.push(e);
        lastInput = e;
      } else if (e.type === 'click' || e.type === 'rage_click' || e.type === 'navigate') {
        lastInput = null;
      }
    }
    return entries;
  }

  prepareSessionSummaryPayload(): string {
    const m = this.sessionMetrics || this.computeSessionMetrics();
    // Lists are capped so long sessions stay within the GET request URL limit
    const navs = this.timelineEvents.filter(e => e.type === 'navigate').slice(0, 20);
    const rageClicks = this.getRageBursts().slice(0, 10);
    const keyClicks = this.timelineEvents
      .filter(e => e.type === 'click' && !e.label.includes('Clicked on screen'))
      .slice(0, 15)
      .map(c => `${c.label} (${this.formatTime(c.timeMs)})`);
    const inputs = this.getFieldEntries()
      .slice(0, 10)
      .map(i => `${i.label} (${this.formatTime(i.timeMs)})`);
    const gaps = this.gapIntervals.slice(0, 10);

    return [
      `Session ID: ${this.sessionId}`,
      `Visitor ID: ${this.visitorId}`,
      `Device & Viewport: ${m.deviceType} (${this.sessionViewport ? this.sessionViewport.width + 'x' + this.sessionViewport.height : 'Unknown'})`,
      `Total Recording Duration: ${m.formattedDuration}`,
      `Active Time: ${m.formattedActiveTime} (${m.activePercent}%) | Idle/Inactivity Time: ${this.formatTime(m.inactiveMs)} (${m.inactivePercent}%)`,
      `Total Clicks: ${m.clickCount} | Form Fields Filled: ${m.inputCount} | Total Page Visits: ${m.navCount}`,
      `Frustration Signals: ${m.rageClickCount} Rage Clicks detected`,
      `Pages Visited:\n${navs.length ? navs.map(n => `- ${n.label} at ${this.formatTime(n.timeMs)}`).join('\n') : `- ${this.sessionUrl || 'Home'}`}`,
      `Key Button/Element Clicks:\n${keyClicks.length ? keyClicks.map(c => `- ${c}`).join('\n') : 'None'}`,
      `Form Interactions:\n${inputs.length ? inputs.map(i => `- ${i}`).join('\n') : 'None'}`,
      `Rage Click Events:\n${rageClicks.length ? rageClicks.map(r => `- ${r.label} at ${this.formatTime(r.timeMs)}`).join('\n') : 'None'}`,
      `Inactivity Gaps (>10s):\n${gaps.length ? gaps.map(g => `- Idle between ${this.formatTime(g.start)} and ${this.formatTime(g.end)} (${Math.round((g.end - g.start)/1000)}s)`).join('\n') : 'None'}`,
    ].join('\n\n');
  }

  generateAiSummary(event?: Event): void {
    if (this.isGeneratingAiSummary) return;
    this.aiPurpose = (event?.currentTarget as HTMLElement)?.dataset?.aiPurpose || this.aiPurpose;

    if (!this.events || !this.events.length) {
      this.utils?.notify?.showInfo?.('No recording events available to summarize.', 'Notice');
      return;
    }

    this.isGeneratingAiSummary = true;
    this.aiSummaryError = null;
    this.resetAiFeedback();
    this.showAiSummaryModal = true;
    this.sessionMetrics = this.computeSessionMetrics();

    const reportData = this.prepareSessionSummaryPayload();
    const params: any = {
      type: 'GENERATE_SESSION_REPLAY_SUMMARY',
      report_data: reportData,
      duration_ms: Math.round(this.sessionDurationMs),
      // Middleware writes the summary in this dashboard language
      language_code: this.translationService.getLanguageCode(),
      desc: 'AI Session Replay Recording Summary & Insights Generation',
    };

    this.svc.getAIGeneratedContent(params).pipe(takeUntil(this.destroy$)).subscribe(
      (res: any) => {
        this.isGeneratingAiSummary = false;
        let content = res?.data;
        if (content && typeof content === 'object') {
          content = JSON.stringify(content, null, 2);
        }
        if (typeof content === 'string' && content.trim()) {
          this.aiSummaryContent = content;
          this.parseAiSummary();
          this.trackAiGenerate('AI Generate Success');
        } else {
          this.setAiSummaryError('The AI returned an empty summary. Please try again.');
        }
        this.loadAiCreditState();
        this.cdr.markForCheck();
      },
      (err: any) => {
        this.isGeneratingAiSummary = false;
        console.error('[replay-player] AI generation failed', err);
        const serverMsg =
          (typeof err?.error?.message === 'string' ? err.error.message : null) ||
          (typeof err?.error?.data?.error === 'string' ? err.error.data.error : null) ||
          (typeof err?.error?.data?.message === 'string' ? err.error.data.message : null) ||
          (typeof err?.error?.error === 'string' ? err.error.error : null) ||
          (typeof err?.error?.message?.error === 'string' ? err.error.message.error : null) ||
          (typeof err?.error?.data?.message?.error === 'string' ? err.error.data.message.error : null) ||
          (err?.status === 414 ? 'Request payload was too large. Please retry.' : null);
        this.setAiSummaryError(
          serverMsg ||
          this.utils?.getErrorMessageWithRefId?.(
            err,
            'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.'
          ) ||
          'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.'
        );
        this.loadAiCreditState();
        this.cdr.markForCheck();
      }
    );
  }

  private setAiSummaryError(message: string): void {
    // Clear the previous summary so reopening the modal retries instead of showing stale content
    this.aiSummaryError = message;
    this.aiSummaryContent = '';
    this.parsedSections = [];
    this.trackAiGenerate('AI Generate Fail', message);
  }

  parseAiSummary(): void {
    this.parsedSections = parseAiInsightSections(this.aiSummaryContent, AI_SESSION_SUMMARY_SECTIONS, { keepBold: true });
  }

  displayUrl(url?: string): string {
    if (!url) return '-';
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  extractRowTimestamp(row: AiInsightRow): { timeStr: string; ms: number } | null {
    const text = `${row.label || ''} ${row.value || ''} ${row.text || ''} ${row.note || ''}`;
    const match = text.match(/\b(?:(?:(\d+):)?([0-5]?\d):([0-5]\d))\b/);
    if (!match) return null;

    const timeStr = match[0];
    const parts = timeStr.split(':').map(Number);
    let ms = 0;
    if (parts.length === 3) {
      ms = (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
    } else if (parts.length === 2) {
      ms = (parts[0] * 60 + parts[1]) * 1000;
    }
    return { timeStr, ms };
  }

  jumpToTimestamp(ms: number): void {
    // The modal overlay covers the player, so close it to show the jump
    this.closeAiSummary();
    this.seekTo(ms);
    this.play();
  }

  getInsightTokens(rawText: string): InsightToken[] {
    if (!rawText) return [];

    const boldParts = rawText.split(/(\*\*[^*]+\*\*)/g);
    const tokens: InsightToken[] = [];

    for (let part of boldParts) {
      if (!part) continue;
      // Bold text is still scanned so a bold timestamp (e.g. "**00:10**") stays clickable
      const isBold = part.startsWith('**') && part.endsWith('**') && part.length > 4;
      if (isBold) part = part.slice(2, -2);

      const tsRegex = /\b(?:(\d{1,2}):([0-5]\d)(?::([0-5]\d))?)\b/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = tsRegex.exec(part)) !== null) {
        if (match.index > lastIndex) {
          tokens.push({ text: part.slice(lastIndex, match.index), isBold });
        }

        const timeStr = match[0];
        const parts = timeStr.split(':').map(Number);
        let ms = 0;
        if (parts.length === 3) {
          ms = (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
        } else if (parts.length === 2) {
          ms = (parts[0] * 60 + parts[1]) * 1000;
        }

        tokens.push({
          text: timeStr,
          isTimestamp: true,
          ms,
        });

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < part.length) {
        tokens.push({ text: part.slice(lastIndex), isBold });
      }
    }

    return tokens;
  }

  shareFeedback(): void {
    this.showFeedbackForm = true;
    this.cdr.markForCheck();
  }

  cancelAiFeedback(): void {
    this.showFeedbackForm = false;
    this.feedbackRating = null;
    this.feedbackComment = '';
    this.feedbackError = null;
    this.cdr.markForCheck();
  }

  private resetAiFeedback(): void {
    this.cancelAiFeedback();
    this.feedbackSubmitted = false;
  }

  submitAiFeedback(): void {
    if (!this.feedbackRating || this.isSubmittingFeedback) return;
    this.isSubmittingFeedback = true;
    this.feedbackError = null;

    this.svc.createAiSummaryFeedback({
      rating: this.feedbackRating,
      comment: this.feedbackComment.trim(),
      session_id: this.sessionId,
      store_id: this.storeId,
    }).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.isSubmittingFeedback = false;
        this.cancelAiFeedback();
        this.feedbackSubmitted = true;
        this.cdr.markForCheck();
      },
      (err: any) => {
        this.isSubmittingFeedback = false;
        // Shown inside the modal: a toast would render behind the overlay
        this.feedbackError =
          this.utils?.getErrorMessageWithRefId?.(err, 'Unable to send feedback. Please try again.') ||
          'Unable to send feedback. Please try again.';
        this.cdr.markForCheck();
      }
    );
  }
}
